import { supabase } from '../lib/supabase';
import { allRows, rows } from '../shelves/load';
import type { CopyFact, LoanFact } from './library';
import type { HistEvent } from './history';
import { checkDaysOf } from '../lib/libraryStates';

// My books › Library copies (Part 2): what the tab reads besides the shelves, as you (owner rules).
// Your copies (enough to tell what you own), every hold and loan still going with its library's
// settings, and your libraries for the Your library line.

type Row = Record<string, unknown>;
const one = <T>(v: unknown): T | null => (Array.isArray(v) ? ((v[0] as T) ?? null) : ((v as T) ?? null));
const num = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

export interface LibraryInfo {
  id: string;
  name: string;
  apps: string[];
  libbyCode: string | null;
  loanDays: number;
  borrowWindow: number;
  checkDays: number;
  renewDays: number;
}

export interface LibraryFacts {
  copies: CopyFact[];
  loans: LoanFact[];
  libraries: LibraryInfo[];
}

export async function loadLibraryFacts(): Promise<LibraryFacts> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const [copies, loans, libraries] = await Promise.all([
    allRows(() => db.from('copies').select('id, book_id, how, state').order('id') as never),
    rows(
      db
        .from('library_loans')
        .select(
          'id, step, hold_placed_on, expected_on, borrow_by, borrowed_on, due_on, place_in_line, library_copies, quiet_until, copy:copies!inner(id, book_id, format, edition:editions(kind)), library:libraries(name, libby_code, check_days_before, renew_days_before)',
        )
        .in('step', ['on_hold', 'ready', 'borrowed']),
    ),
    rows(db.from('libraries').select('id, name, lending_apps, libby_code, loan_days, borrow_window_days, check_days_before, renew_days_before, sort_order').order('sort_order')),
  ]);
  return {
    copies: copies.map((c) => ({ bookId: c.book_id as string, how: c.how as string, state: c.state as string })),
    loans: loans.map((l): LoanFact => {
      const c = one<Row>(l.copy)!;
      const lib = one<Row>(l.library);
      const kind = one<Row>(c.edition)?.kind as string | undefined;
      return {
        id: l.id as string,
        copyId: c.id as string,
        bookId: c.book_id as string,
        format: c.format as string,
        listen: kind === 'audiobook',
        library: str(lib?.name),
        libbyCode: str(lib?.libby_code),
        step: l.step as LoanFact['step'],
        holdPlacedOn: str(l.hold_placed_on),
        expectedOn: str(l.expected_on),
        borrowBy: str(l.borrow_by),
        borrowedOn: str(l.borrowed_on),
        dueOn: str(l.due_on),
        placeInLine: num(l.place_in_line),
        libraryCopies: num(l.library_copies),
        checkDays: checkDaysOf(lib?.check_days_before),
        renewDays: num(lib?.renew_days_before) ?? 3,
        quietUntil: str(l.quiet_until),
      };
    }),
    libraries: libraries.map((l) => ({
      id: l.id as string,
      name: (l.name as string) ?? 'Your library',
      apps: ((l.lending_apps as string[] | null) ?? []).filter(Boolean),
      libbyCode: str(l.libby_code),
      loanDays: num(l.loan_days) ?? 21,
      borrowWindow: num(l.borrow_window_days) ?? 3,
      checkDays: checkDaysOf(l.check_days_before),
      renewDays: num(l.renew_days_before) ?? 3,
    })),
  };
}

/** Library history (step 2): every library step you have, with its book's title. */
export async function loadLibraryEvents(): Promise<HistEvent[]> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const list = await allRows(
    () =>
      db
        .from('library_events')
        .select('id, kind, happened_on, due_on, percent, created_at, loan:library_loans!inner(id, copy:copies!inner(id, book_id, format, edition:editions(kind), book:books!inner(label_number, removed_at, work:works(title))))')
        .order('happened_on', { ascending: false, nullsFirst: false })
        .order('id') as never,
  );
  const out: HistEvent[] = [];
  for (const e of list) {
    const loan = one<Row>(e.loan);
    const copy = one<Row>(loan?.copy);
    const book = one<Row>(copy?.book);
    if (!loan || !copy || !book || book.removed_at) continue;
    out.push({
      id: e.id as string,
      loanId: loan.id as string,
      bookId: copy.book_id as string,
      label: Number(book.label_number),
      title: (one<Row>(book.work)?.title as string) ?? '',
      format: copy.format as string,
      listen: (one<Row>(copy.edition)?.kind as string) === 'audiobook',
      kind: e.kind as string,
      on: str(e.happened_on),
      dueOn: str(e.due_on),
      percent: num(e.percent),
      createdAt: (e.created_at as string) ?? '',
    });
  }
  return out;
}
